import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDateTime } from '../i18n.js';
import { useMe } from '../session.js';
import { useLocations } from './Availability.js';
import { LinkButton } from './ui.js';

/**
 * The second check location for one site (T-67, site-incidents.html): how many suspected outages it
 * confirmed and how many false alarms it avoided. Shown only while the second location actually
 * runs (spec §17); lower plans see the offer then, never before (audit §3).
 */
export function LocationsCard({ siteId }: { siteId: string }) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const timeZone = useMe().data?.account.timezone;
  const l = useLocations(siteId).data;
  if (!l) return null;
  const total = l.confirmed + l.avoided + l.unavailable;
  if (!l.available)
    return l.active ? (
      <section className="card">
        <div className="upsell">
          <span>{t('locations.planNeeded')}</span>
          <LinkButton to="/billing" variant="secondary" size="sm">
            {t('availability.plans')}
          </LinkButton>
        </div>
      </section>
    ) : null;
  if (!l.active && total === 0) return null;

  const stats = [
    { key: 'confirmed', value: l.confirmed },
    { key: 'avoided', value: l.avoided },
    ...(l.unavailable > 0 ? [{ key: 'unavailable', value: l.unavailable }] : []),
  ] as const;
  const [latest, ...earlier] = l.recent;
  const when = (iso: string) => formatDateTime(iso, i18n.language, timeZone);

  return (
    <section className="card locations" aria-labelledby={titleId}>
      <h2 id={titleId}>{t('locations.title')}</h2>
      <p className="bodyM">{t('locations.intro')}</p>
      {total === 0 ? (
        <p className="bodyM">{t('locations.none', { count: l.days })}</p>
      ) : (
        <>
          <p className="hint">{t('locations.period', { count: l.days })}</p>
          <dl className="kv">
            {stats.map((s) => (
              <div key={s.key}>
                <dt>{t(`locations.${s.key}`)}</dt>
                <dd className={`mono${s.key === 'avoided' && s.value > 0 ? ' good' : ''}`}>
                  {s.value}
                </dd>
              </div>
            ))}
            {latest ? (
              <div>
                <dt>{t('locations.latest')}</dt>
                <dd>
                  {t(`locations.verdicts.${latest.verdict}`)} · {when(latest.checkedAt)}
                </dd>
              </div>
            ) : null}
          </dl>
          {earlier.length > 0 ? (
            <>
              <h3 className="h4s">{t('locations.recent')}</h3>
              <ul className="recent">
                {earlier.map((r) => (
                  <li key={r.checkedAt}>
                    <span className="mono">{when(r.checkedAt)}</span>
                    <span>{t(`locations.verdicts.${r.verdict}`)}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </>
      )}
      {!l.active ? <p className="hint">{t('locations.paused')}</p> : null}
    </section>
  );
}
